<template>
  <v-card>
    <v-alert :type="alertType" v-if="alert">{{ alertMessage }}</v-alert>
    <v-toolbar flat>
      <v-toolbar-title class="headline">Contact Activity</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click.native.stop="openNewEvent"
        >New Event</v-btn
      >
    </v-toolbar>
    <v-container class="py-0" fluid>
      <v-row align-content="space-around">
        <v-spacer></v-spacer>
        <v-col cols="8" sm="4">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            hide-details
            single-line
            clearable
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>
    <v-data-table
      :footer-props="{
        'items-per-page-options': [50]
      }"
      :headers="headers"
      :items="contactEvents"
      :options.sync="tableOptions"
      :server-items-length="contactEventsTotal"
      item-key="id"
      :loading="eventsLoading"
    >
      <template v-slot:item.saId="{ item }">
        <a
          class="go-to-case-link mr-2"
          target="_blank"
          :href="
            `https://cs-web.airdesksolutions.com/SA/Edit_Contact.aspx?ID=${item.saId}&CTG=${item.saCtg}`
          "
        >
          {{ item.saId }}
        </a>
      </template>
      <template v-slot:item.timestamp="{ item }">
        {{ formatDate(item) }}
      </template>
      <template v-slot:item.user.firstName="{ item }">
        {{ item.user.firstName + " " + item.user.lastName }}
      </template>
      <template v-slot:item.expenseAmount="{ item }">
        {{ formatExpense(item.expenseAmount) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon
          v-if="canEditEvent(item)"
          small
          @click="editEvent(item)"
        >
          mdi-pencil
        </v-icon>
      </template>
    </v-data-table>
    <v-dialog v-model="eventFormDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">
          {{ editingEventId ? "Edit Event" : "New Event" }}
        </v-card-title>
        <v-card-text>
          <div class="overline mb-1">When</div>
          <date-picker
            v-model="eventFormDate"
            valueType="timestamp"
            type="datetime"
            format="MMMM D YYYY, h:mm a"
            placeholder="Event Date"
            :minute-step="15"
            class="event-date-picker mb-4"
          ></date-picker>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="eventFormEventType"
                :items="eventTypeOptions"
                label="Type"
                outlined
                hide-details
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="eventFormContact"
                :items="contactOptions"
                label="Contact"
                outlined
                hide-details
              ></v-autocomplete>
            </v-col>
          </v-row>

          <v-textarea
            class="mt-4"
            label="Comment"
            v-model="eventFormComment"
            outlined
            rows="3"
            hide-details
          ></v-textarea>

          <v-switch
            v-model="eventFormSpentMoney"
            label="Spent money on this"
            hide-details
            class="mt-4"
          ></v-switch>
          <v-text-field
            v-if="eventFormSpentMoney"
            v-model="eventFormExpenseAmount"
            label="Amount"
            type="number"
            prefix="$"
            step="0.01"
            min="0"
            outlined
            hide-details
            class="mt-2"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn text @click="eventFormDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveEvent"
            :disabled="formDisabled"
            :loading="saving"
          >
            {{ editingEventId ? "Save" : "Add Event" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import sortBy from "lodash/sortBy";
import debounce from "lodash/debounce";
import format from "date-fns/format";
import { mapGetters } from "vuex";
import { hasRole } from "../util";

export default {
  data() {
    return {
      alert: false,
      alertType: false,
      alertMessage: false,
      editingEventId: null,
      eventFormComment: "",
      eventFormContact: "",
      eventFormDialog: false,
      eventFormDate: "",
      eventFormDateMenu: false,
      eventFormEventType: "",
      eventFormSpentMoney: false,
      eventFormExpenseAmount: null,
      saving: false,
      search: "",
      debouncedSearch: "",
      tableOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ["timestamp"],
        sortDesc: [true]
      }
    };
  },
  components: {
    DatePicker
  },
  watch: {
    eventFormDialog(open) {
      if (!open) this.resetEventForm();
    },
    search() {
      this.queueSearch();
    }
  },
  methods: {
    openNewEvent() {
      this.eventFormDialog = true;
    },
    queueSearch: debounce(function() {
      this.debouncedSearch = this.search ? this.search.trim() : "";
      if (this.tableOptions.page !== 1) {
        this.tableOptions.page = 1;
      }
    }, 300),
    refreshEvents() {
      this.tableOptions.page = 1;
      this.$apollo.queries.contactEventsPage.refetch();
    },
    editEvent(item) {
      this.editingEventId = item.id;
      this.eventFormDate = item.timestamp
        ? parseInt(item.timestamp, 10)
        : "";
      this.eventFormEventType = item.contactEventType
        ? item.contactEventType.id
        : "";
      this.eventFormContact = item.contact ? item.contact.id : "";
      this.eventFormComment = item.comment || "";
      let expense = parseFloat(item.expenseAmount);
      this.eventFormSpentMoney = Number.isFinite(expense) && expense > 0;
      this.eventFormExpenseAmount = this.eventFormSpentMoney
        ? item.expenseAmount
        : null;
      this.eventFormDialog = true;
    },
    resetEventForm() {
      this.editingEventId = null;
      this.eventFormComment = "";
      this.eventFormContact = "";
      this.eventFormDate = "";
      this.eventFormEventType = "";
      this.eventFormSpentMoney = false;
      this.eventFormExpenseAmount = null;
      this.saving = false;
    },
    eventPayload() {
      let expenseAmount =
        this.parsedExpenseAmount != null
          ? this.parsedExpenseAmount.toFixed(2)
          : null;
      return {
        timestamp: this.eventFormDate.toString(),
        contactEventTypeId: parseInt(this.eventFormEventType, 10),
        contactId: parseInt(this.eventFormContact, 10),
        comment: this.eventFormComment,
        expenseAmount
      };
    },
    saveEvent() {
      if (this.editingEventId) {
        this.updateEvent();
      } else {
        this.addEvent();
      }
    },
    addEvent() {
      this.saving = true;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation CreateContactEvent($contactEvent: ContactEventInput!) {
              createContactEvent(contactEvent: $contactEvent) {
                id
              }
            }
          `,
          variables: {
            contactEvent: this.eventPayload()
          },
          update: () => {
            this.refreshEvents();
          }
        })
        .then(() => {
          this.eventFormDialog = false;
        })
        .catch(error => {
          this.saving = false;
          console.error(error);
          this.showAlert("error", error.message);
        });
      return false;
    },
    updateEvent() {
      this.saving = true;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation UpdateContactEvent(
              $contactEventId: Int!
              $contactEvent: ContactEventInput!
            ) {
              updateContactEvent(
                contactEventId: $contactEventId
                contactEvent: $contactEvent
              ) {
                id
              }
            }
          `,
          variables: {
            contactEventId: parseInt(this.editingEventId, 10),
            contactEvent: this.eventPayload()
          },
          update: () => {
            this.refreshEvents();
          }
        })
        .then(() => {
          this.eventFormDialog = false;
        })
        .catch(error => {
          this.saving = false;
          console.error(error);
          this.showAlert("error", error.message);
        });
    },
    showAlert(type, message) {
      this.alert = true;
      this.alertType = type;
      this.alertMessage = message;
      setTimeout(() => {
        this.alert = false;
      }, 3000);
    },
    canEditEvent(item) {
      if (!this.currentUser || !item || !item.user) return false;
      if (this.isIntakeAdmin) return true;
      if (hasRole(this.currentUser, "overlord")) return true;
      return (
        hasRole(this.currentUser, "intake") &&
        parseInt(item.user.id, 10) === parseInt(this.currentUser.id, 10)
      );
    },
    formatDate(item) {
      return format(new Date(parseInt(item.timestamp, 10)), "MMM dd, yyyy");
    },
    formatExpense(amount) {
      if (amount == null || amount === "") return "";
      let value = parseFloat(amount);
      if (!Number.isFinite(value) || value <= 0) return "";
      return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD"
      });
    }
  },
  computed: {
    ...mapGetters(["currentUser", "isIntakeAdmin"]),
    headers() {
      let headers = [
        { text: "Date", value: "timestamp", width: "100px", sortable: false },
        {
          text: "Type",
          value: "contactEventType.name",
          width: "80px",
          sortable: false
        },
        { text: "Points", value: "points", width: "80px", sortable: false },
        {
          text: "Expense",
          value: "expenseAmount",
          width: "100px",
          sortable: false
        },
        {
          text: "User",
          value: "user.firstName",
          width: "150px",
          sortable: false
        },
        {
          text: "Contact",
          value: "contact.name",
          width: "200px",
          sortable: false
        },
        { text: "Comment", value: "comment", sortable: false }
      ];
      if (this.canEditAnyEvent) {
        headers.push({
          text: "",
          value: "actions",
          sortable: false,
          width: "50px"
        });
      }
      return headers;
    },
    canEditAnyEvent() {
      if (!this.currentUser) return false;
      return (
        this.isIntakeAdmin ||
        hasRole(this.currentUser, "intake") ||
        hasRole(this.currentUser, "overlord")
      );
    },
    contactEvents() {
      return (this.contactEventsPage && this.contactEventsPage.entries) || [];
    },
    contactEventsTotal() {
      return (this.contactEventsPage && this.contactEventsPage.totalCount) || 0;
    },
    pageSize() {
      let size = this.tableOptions.itemsPerPage;
      return size > 0 ? size : 50;
    },
    pageOffset() {
      return (Math.max(this.tableOptions.page, 1) - 1) * this.pageSize;
    },
    eventsLoading() {
      return (
        this.$apollo.queries.contactEventsPage &&
        this.$apollo.queries.contactEventsPage.loading
      );
    },
    parsedExpenseAmount() {
      if (
        !this.eventFormSpentMoney ||
        this.eventFormExpenseAmount == null ||
        this.eventFormExpenseAmount === ""
      ) {
        return null;
      }
      let amount = parseFloat(this.eventFormExpenseAmount);
      return Number.isFinite(amount) && amount > 0 ? amount : null;
    },
    eventTypeOptions() {
      if (!this.contactEventTypes) return [];
      return sortBy(
        this.contactEventTypes.map(c => {
          return {
            text: c.name,
            value: c.id
          };
        }),
        "text"
      );
    },
    contactOptions() {
      if (!this.contacts) return [];
      return sortBy(
        this.contacts.map(c => {
          return {
            text: c.name,
            value: c.id
          };
        }),
        "text"
      );
    },
    formDisabled() {
      if (this.saving) return true;
      if (!this.eventFormEventType) return true;
      if (!this.eventFormContact) return true;
      if (!this.eventFormDate) return true;
      if (!this.eventFormComment) return true;
      if (this.eventFormSpentMoney && this.parsedExpenseAmount == null)
        return true;
      return false;
    }
  },
  apollo: {
    contactEventsPage: {
      query: gql`
        query CRMActivity($limit: Int, $offset: Int, $search: String) {
          contactEvents(limit: $limit, offset: $offset, search: $search) {
            totalCount
            limit
            offset
            entries {
              id
              comment
              timestamp
              points
              expenseAmount
              contactEventType {
                id
                name
              }
              user {
                id
                firstName
                lastName
              }
              contact {
                id
                name
              }
            }
          }
        }
      `,
      variables() {
        return {
          limit: this.pageSize,
          offset: this.pageOffset,
          search: this.debouncedSearch || null
        };
      },
      update(data) {
        return data.contactEvents;
      },
      fetchPolicy: "cache-and-network"
    },
    contactEventTypes: {
      query: gql`
        query ContactEventTypes {
          contactEventTypes {
            id
            name
          }
        }
      `
    },
    contacts: {
      query: gql`
        query CRMContacts {
          contacts {
            id
            name
          }
        }
      `
    }
  }
};
</script>

<style scoped lang="scss">
.event-date-picker {
  width: 100%;
}
.event-date-picker ::v-deep .mx-input {
  height: 40px;
  border-radius: 4px;
  font-size: 16px;
}
</style>
